import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  ApiError,
  AuthService,
  BaseVisualizacaoService,
  ConfigService,
  MessageService,
  Task,
  TaskService
} from '../../../../../core';
import { IMenuSettings } from '../../../../../domains/shared/components/menu/interfaces/menu-settings.interface';
import { ISkeletonColumn } from '../../../../../domains/shared/components/table-skeleton/table-skeleton.component';
import { ITableSettings } from '../../../../../domains/shared/components/table/interfaces/table-settings.interface';
import {
  ClienteCarteiraBaixarHttpService,
  ClienteCarteiraBaixarModalComponent
} from '../../../../cliente-carteira/baixar';
import {
  ClienteCarteiraCongelarHttpService,
  ClienteCarteiraCongelarModalComponent
} from '../../../../cliente-carteira/congelar';
import {
  ClienteCarteiraPreBaixarHttpService,
  ClienteCarteiraPreBaixarModalComponent
} from '../../../../cliente-carteira/prebaixar';
import {
  ConfigEmpresa,
  isConfigEmpresaVersaoIntegracaoErp1
} from '../../../../configuracoes/app/empresa';
import { IntegrationClienteHttpService } from '../../../../configuracoes/integration/cliente';
import { funilVendaFaseTipoOportunidade } from '../../../../crm/funil-venda/fase';
import { NegocioFormInsertModalComponent } from '../../../../crm/negocio/form-insert';
import { ClienteAnaliseFinanceiraConcluirModalComponent } from '../../analise-financeira/concluir-modal';
import { ClienteAnaliseFinanceiraRevisarModalComponent } from '../../analise-financeira/revisar-modal';
import {
  ClienteAnaliseFinanceiraAuthGuardService,
  ClienteAnaliseFinanceiraConcluirHttpService,
  ClienteAnaliseFinanceiraIniciarHttpService
} from '../../analise-financeira/services';
import { Cliente } from '../../cliente';
import { ClienteSearchOrderBy } from '../../cliente-search-orderby';
import {
  ClienteOptionsActions
} from '../../cliente-table-options';
import { ClienteFilter } from '../../filter';
import { ClienteIntegracaoErpImportHttpService } from '../../integracoes/erp/import';
import { ClienteNomePipe } from '../../pipes/cliente-nome.pipe';
import {
  ClienteAuthGuardService,
  ClienteCrudHttpService,
  ClienteReportService
} from '../../services';

const PAGE_SIZE = 50;

export interface IClienteTableAction {
  key: string;
  icon: string;
  label: string;
  inline?: true;
  groupLabel?: string;
  divider?: true;
  handler: () => void;
}


@Component({
  selector: 'sgt-novo-cliente-list',
  templateUrl: './novo-cliente-list.component.html',
  styleUrls: ['./novo-cliente-list.component.scss'],
  providers: [ClienteNomePipe]
})
export class NovoClienteListComponent implements OnInit, OnChanges, OnDestroy {
  private sub$: Subscription;
  private isInitialLoad = false;
  configEmpresa: ConfigEmpresa;
  isIntegracaoErpV1: boolean;
  isAdministrador: boolean;
  isAtendente: boolean;
  isRepresentante: boolean;
  isFinanceiro: boolean;
  canVisualizarAnaliseFinanceira: boolean;

  clientes: Cliente[] = [];
  inlineActionsMap = new Map<number, IClienteTableAction[]>();
  dropdownActionsMap = new Map<number, IClienteTableAction[]>();
  totalClientes = 0;
  private currentOffset = 0;
  private isAllLoaded = false;
  isLoadingMore = false;

  settings: ITableSettings = {
    loading: true,
    isEmpty: false,
    showFooter: true,
    customContainerClass: 'table-modern table-modern--condensed table-full-height',
    customTableClass: 'table-wide',
  };

  readonly menuSettings: IMenuSettings = {
    closeOnSelect: true,
    isToCloseMenuOnScroll: true
  };

  readonly skeletonColumns: ISkeletonColumn[] = [
    { width: '120px', label: '' },
    { width: '260px', label: '' },
    { width: '180px', label: '' },
    { width: '160px', label: '' },
    { width: '140px', label: '' },
    { width: '100px', label: '' }
  ];

  @Input() optionsVisibility: Partial<ClienteOptionsActions> = {
    canEdit: true,
    canPrintFichaCadastral: true,
    canImportOneErp_v1: true,
    canImportOneErp: true,
    canInsertNegocio: true,
    canCongelar: true,
    canDescongelar: true,
    canPreBaixar: true,
    canEditPreBaixa: true,
    canCancelarPreBaixa: true,
    canBaixar: true,
    canEditBaixa: true,
    canCancelarBaixa: true
  };

  @Input() filter = new ClienteFilter();
  readonly orderByEnum = ClienteSearchOrderBy;
  orderBy: number = ClienteSearchOrderBy.Nome;

  readonly columns: Array<{
    label: string;
    orderBy?: number;
    css?: string;
    hidden?: () => boolean;
  }> = [
    { label: 'Código ERP', orderBy: ClienteSearchOrderBy.CodigoIntegracaoErp },
    { label: 'Nome', orderBy: ClienteSearchOrderBy.Nome },
    { label: 'Nome Fantasia', orderBy: ClienteSearchOrderBy.NomeFantasia },
    { label: 'CNPJ / CPF', orderBy: ClienteSearchOrderBy.CnpjCpf },
    {
      label: 'Representante',
      orderBy: ClienteSearchOrderBy.RepresentanteNome,
      hidden: () => this.isRepresentante
    },
    { label: 'Gerente', orderBy: ClienteSearchOrderBy.GerenteNome },
    { label: 'Atendente', orderBy: ClienteSearchOrderBy.AtendenteNome },
    {
      label: 'Tipo',
      orderBy: ClienteSearchOrderBy.TipoPessoa,
      css: 'text-center'
    },
    {
      label: 'Cadastro',
      orderBy: ClienteSearchOrderBy.Cadastro,
      css: 'text-center'
    },
    {
      label: 'Situação',
      orderBy: ClienteSearchOrderBy.Situacao,
      css: 'text-center'
    },
    { label: 'Localização', css: 'col-localizacao' },
    { label: 'Origem', orderBy: ClienteSearchOrderBy.Origem },
    {
      label: 'Canal Mídia Social',
      orderBy: ClienteSearchOrderBy.CanalMidiaSocial
    },
    {
      label: 'Campanha de Origem',
      orderBy: ClienteSearchOrderBy.NomeCampanhaOrigem
    },
    {
      label: 'Campanha de Aquisição',
      orderBy: ClienteSearchOrderBy.NomeCampanhaAquisicao
    },
    {
      label: 'Análise Financeira',
      css: 'text-center',
      hidden: () => !this.canVisualizarAnaliseFinanceira
    }
  ];
  orderByDir = 'asc';

  @ViewChild('analiseFinanceiraConcluirModal')
  analiseFinanceiraConcluirModal: ClienteAnaliseFinanceiraConcluirModalComponent;
  @ViewChild('analiseFinanceiraRevisarModal')
  analiseFinanceiraRevisarModal: ClienteAnaliseFinanceiraRevisarModalComponent;
  @ViewChild('clienteCarteiraCongelarModal')
  clienteCarteiraCongelarModal: ClienteCarteiraCongelarModalComponent;
  @ViewChild('clienteCarteiraPreBaixarModal')
  clienteCarteiraPreBaixarModal: ClienteCarteiraPreBaixarModalComponent;
  @ViewChild('clienteCarteiraBaixarModal')
  clienteCarteiraBaixarModal: ClienteCarteiraBaixarModalComponent;
  @ViewChild('formInsertModal')
  formInsertModal: NegocioFormInsertModalComponent;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private baseVisualizacaoService: BaseVisualizacaoService,
    private configService: ConfigService,
    private messageService: MessageService,
    private taskService: TaskService,
    private clienteNomePipe: ClienteNomePipe,
    private clienteAuthGuardService: ClienteAuthGuardService,
    private clienteCrudHttpService: ClienteCrudHttpService,
    private clienteReportService: ClienteReportService,
    private clienteAnaliseFinanceiraAuthGuardService: ClienteAnaliseFinanceiraAuthGuardService,
    private clienteAnaliseFinanceiraIniciarHttpService: ClienteAnaliseFinanceiraIniciarHttpService,
    private clienteAnaliseFinanceiraConcluirHttpService: ClienteAnaliseFinanceiraConcluirHttpService,
    private clienteCarteiraCongelarHttpService: ClienteCarteiraCongelarHttpService,
    private clienteCarteiraPreBaixarHttpService: ClienteCarteiraPreBaixarHttpService,
    private clienteCarteiraBaixarHttpService: ClienteCarteiraBaixarHttpService,
    private integrationClienteHttpService: IntegrationClienteHttpService,
    private clienteIntegracaoErpImportHttpService: ClienteIntegracaoErpImportHttpService
  ) {}

  ngOnInit() {
    this.configEmpresa = this.configService.getEmpresa();
    this.isIntegracaoErpV1 = isConfigEmpresaVersaoIntegracaoErp1(
      this.configEmpresa
    );
    this.isAdministrador = this.authService.isAdministrador();
    this.isAtendente = this.authService.isAtendente();
    this.isRepresentante = this.authService.isRepresentante();
    this.isFinanceiro = this.authService.isFinanceiro();
    this.canVisualizarAnaliseFinanceira =
      this.clienteAnaliseFinanceiraAuthGuardService.canVisualizar();
    this.search();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filter'] && !changes['filter'].firstChange) {
      this.search();
    }
  }

  sort(column: number) {
    if (this.orderBy === column) {
      this.orderByDir = this.orderByDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.orderBy = column;
      this.orderByDir = 'asc';
    }
    this.search();
  }

  search() {
    this.currentOffset = 0;
    this.isAllLoaded = false;
    this.totalClientes = 0;
    this.isInitialLoad = true;
    this.loadMore();
  }

  refresh() {
    this.search();
  }

  onScrollBottom() {
    if (this.settings.loading || this.isLoadingMore || this.isAllLoaded) {
      return;
    }
    this.loadMore();
  }

  private loadMore() {
    this.sub$?.unsubscribe();

    if (this.isInitialLoad) {
      if (this.clientes.length === 0) {
        this.settings = { ...this.settings, loading: true, isEmpty: false };
      }
    } else {
      this.isLoadingMore = true;
    }

    const filter = {
      data: this.filter,
      offset: this.currentOffset,
      limit: PAGE_SIZE,
      orderBy: this.orderBy,
      orderByDir: this.orderByDir
    };

    this.sub$ = this.clienteCrudHttpService
      .searchWithFilter(filter.data, 0, filter.offset, filter.limit, filter.orderBy, filter.orderByDir)
      .subscribe(
        (response) => {
          this.totalClientes = response?.recordsTotal ?? 0;
          const newData = response?.data ?? [];
          if (this.isInitialLoad) {
            this.isInitialLoad = false;
            this.clientes = newData;
            this.inlineActionsMap.clear();
            this.dropdownActionsMap.clear();
          } else {
            this.clientes = [...this.clientes, ...newData];
          }
          newData.forEach((c) => {
            const actions = this.getTableOptions(c);
            this.inlineActionsMap.set(c.id, actions.filter(a => a.inline));
            this.dropdownActionsMap.set(c.id, actions.filter(a => !a.inline));
          });
          this.currentOffset += newData.length;
          this.isAllLoaded = this.clientes.length >= this.totalClientes;
          this.settings = {
            ...this.settings,
            loading: false,
            isEmpty: this.clientes.length === 0
          };
          this.isLoadingMore = false;
        },
        (error: ApiError) => {
          this.messageService.error(
            error.message,
            'Erro ao pesquisar os clientes',
            error
          );
          this.settings = { ...this.settings, loading: false };
          this.isLoadingMore = false;
        }
      );
  }

  private getTableOptions(entity: Cliente): IClienteTableAction[] {
    const v = this.optionsVisibility;
    type Def = IClienteTableAction & { isVisible: boolean };
    const defs: Def[] = [
      {
        isVisible: v.canEdit,
        key: 'edit',
        icon: 'fas fa-pencil-alt',
        label: 'Editar',
        inline: true,
        handler: () => this.edit(entity),
      },
      {
        isVisible: v.canPrintFichaCadastral,
        key: 'printFichaCadastral',
        icon: 'fas fa-print',
        label: 'Imprimir Ficha Cadastral',
        handler: () => this.printFichaCadastral(entity),
      },
      {
        isVisible: v.canInsertNegocio && this.clienteAuthGuardService.canInsertNegocio(entity.cadastro),
        key: 'insertNegocio',
        icon: 'fas fa-bullseye',
        label: 'Criar Oportunidade',
        divider: true,
        handler: () => this.insertNegocio(entity),
      },
      {
        isVisible: v.canCongelar && this.clienteAuthGuardService.canCongelar(entity.cadastro, entity.situacao),
        key: 'congelar',
        icon: 'fas fa-snowflake',
        label: 'Congelar Cliente',
        divider: true,
        handler: () => this.congelar(entity),
      },
      {
        isVisible: v.canCongelar && this.clienteAuthGuardService.canDescongelar(entity.cadastro, entity.situacao),
        key: 'descongelar',
        icon: 'fas fa-snowflake',
        label: 'Descongelar Cliente',
        handler: () => this.descongelar(entity),
      },
      {
        isVisible: v.canPreBaixar && this.clienteAuthGuardService.canPreBaixar(entity.cadastro),
        key: 'preBaixar',
        icon: 'far fa-window-close',
        label: 'Pré-Baixar Cliente',
        groupLabel: 'Pré-Baixar',
        handler: () => this.preBaixar(entity),
      },
      {
        isVisible: v.canEditPreBaixa && this.clienteAuthGuardService.canEditPreBaixa(entity.cadastro, entity.situacao),
        key: 'editPreBaixa',
        icon: 'far fa-edit',
        label: 'Editar Pré-Baixa',
        handler: () => this.editPreBaixa(entity),
      },
      {
        isVisible: v.canCancelarPreBaixa && this.clienteAuthGuardService.canCancelarPreBaixa(entity.cadastro, entity.situacao),
        key: 'cancelarPreBaixa',
        icon: 'fas fa-undo-alt',
        label: 'Cancelar Pré-Baixa',
        handler: () => this.cancelarPreBaixa(entity),
      },
      {
        isVisible: v.canBaixar && this.clienteAuthGuardService.canBaixar(entity.cadastro, entity.situacao, this.isAdministrador, this.isAtendente, this.isFinanceiro),
        key: 'baixar',
        icon: 'far fa-window-close',
        label: 'Baixar Cliente',
        groupLabel: 'Baixar',
        handler: () => this.baixar(entity),
      },
      {
        isVisible: v.canEditBaixa && this.clienteAuthGuardService.canEditBaixa(entity.cadastro, entity.situacao, this.isAdministrador, this.isAtendente, this.isFinanceiro),
        key: 'editBaixa',
        icon: 'far fa-edit',
        label: 'Editar Baixa',
        handler: () => this.editBaixa(entity),
      },
      {
        isVisible: v.canCancelarBaixa && this.clienteAuthGuardService.canCancelarBaixa(entity.cadastro, entity.situacao, this.isAdministrador, this.isAtendente, this.isFinanceiro),
        key: 'cancelarBaixa',
        icon: 'fas fa-undo-alt',
        label: 'Cancelar Baixa',
        handler: () => this.cancelarBaixa(entity),
      },
      {
        isVisible: v.canImportOneErp_v1 && this.canImportOneErp_v1(entity),
        key: 'importOneErp_v1',
        icon: 'fas fa-download',
        label: 'Importar do ERP',
        divider: true,
        handler: () => this.importOneErp_v1(entity),
      },
      {
        isVisible: v.canImportOneErp && this.canImportOneErp(entity),
        key: 'importOneErp',
        icon: 'fas fa-download',
        label: 'Importar do ERP',
        divider: true,
        handler: () => this.importOneErp(entity),
      },
    ];
    return defs.filter(d => d.isVisible);
  }

  trackById(_: number, cliente: Cliente): number {
    return cliente.id;
  }

  edit(entity: Cliente) {
    this.baseVisualizacaoService.edit(entity.id, this.activatedRoute);
  }

  printFichaCadastral(entity: Cliente) {
    if (!entity) {
      return;
    }
    this.clienteReportService.printFichaCadastral(entity.id);
  }

  insertNegocio(entity: Cliente) {
    if (!entity) {
      return;
    }
    this.formInsertModal.insert(
      null,
      funilVendaFaseTipoOportunidade,
      null,
      entity.id
    );
  }

  private canImportOneErp_v1(entity: Cliente): boolean {
    if (!entity || !this.isIntegracaoErpV1) {
      return false;
    }
    if (!entity.pessoa?.codigoIntegracaoErp) {
      return false;
    }
    if (!entity.integradoErp) {
      return false;
    }
    return this.authService.podeImportarErp();
  }

  private canImportOneErp(entity: Cliente): boolean {
    if (!entity || this.isIntegracaoErpV1) {
      return false;
    }
    if (!entity.idIntegracaoErp) {
      return false;
    }
    return this.authService.podeImportarErp();
  }

  importOneErp_v1(entity: Cliente) {
    if (!entity) {
      return;
    }
    const codigoIntegracaoErp = entity.pessoa?.codigoIntegracaoErp ?? '';
    const task = new Task(
      `Importando cliente ${codigoIntegracaoErp}`,
      `Executando importação do cliente ${codigoIntegracaoErp} do ERP`
    );
    this.taskService.add(task, task.description);
    this.integrationClienteHttpService
      .execReadIndividualIntegration(entity.id)
      .subscribe(
        () => {
          this.taskService.success(
            task,
            `Importação do cliente ${codigoIntegracaoErp} do sistema ERP realizada com sucesso`
          );
          this.refresh();
        },
        (error: ApiError) =>
          this.taskService.error(task, error.message, error.cause, error)
      );
  }

  importOneErp(entity: Cliente) {
    if (!entity) {
      return;
    }
    const codigoIntegracaoErp = entity.pessoa?.codigoIntegracaoErp ?? '';
    const task = new Task(
      `Importando cliente ${codigoIntegracaoErp}`,
      `Executando importação do cliente ${codigoIntegracaoErp} do ERP`
    );
    this.taskService.add(task, task.description);
    this.clienteIntegracaoErpImportHttpService.importOne(entity.id).subscribe(
      () => {
        this.taskService.success(
          task,
          `Importação do cliente ${codigoIntegracaoErp} do sistema ERP realizada com sucesso`
        );
        this.refresh();
      },
      (error: ApiError) =>
        this.taskService.error(task, error.message, error.cause, error)
    );
  }

  iniciarAnaliseFinanceira(entity: Cliente) {
    const nomeCliente = this.clienteNomePipe.transform(
      entity.pessoa?.nome,
      entity.pessoa?.nomeFantasia,
      entity.pessoa?.codigoIntegracaoErp
    );
    this.messageService
      .confirm(
        `Deseja realmente iniciar a análise financeira do cliente ${nomeCliente}?`
      )
      .then((result) => {
        if (result) {
          this.clienteAnaliseFinanceiraIniciarHttpService
            .iniciar(entity.id)
            .pipe(take(1))
            .subscribe(
              () => {
                this.messageService.success(
                  '',
                  `Análise Financeira do cliente ${nomeCliente} iniciada com sucesso`
                );
                this.refresh();
              },
              (error: ApiError) =>
                this.messageService.error(error.message, error.cause, error)
            );
        }
      });
  }

  cancelarIniciarAnaliseFinanceira(entity: Cliente) {
    const nomeCliente = this.clienteNomePipe.transform(
      entity.pessoa?.nome,
      entity.pessoa?.nomeFantasia,
      entity.pessoa?.codigoIntegracaoErp
    );
    this.messageService
      .confirm(
        `Deseja realmente cancelar o inicio da análise financeira do cliente ${nomeCliente}?`
      )
      .then((result) => {
        if (result) {
          this.clienteAnaliseFinanceiraIniciarHttpService
            .cancelarIniciar(entity.id)
            .pipe(take(1))
            .subscribe(
              () => {
                this.messageService.success(
                  '',
                  `Inicio da Análise Financeira do cliente ${nomeCliente} cancelada com sucesso`
                );
                this.refresh();
              },
              (error: ApiError) =>
                this.messageService.error(error.message, error.cause, error)
            );
        }
      });
  }

  concluirAnaliseFinanceira(entity: Cliente) {
    this.analiseFinanceiraConcluirModal?.open(entity?.id);
  }

  onAfterConcluirAnaliseFinanceira() {
    this.refresh();
  }

  cancelarConcluirAnaliseFinanceira(entity: Cliente) {
    const nomeCliente = this.clienteNomePipe.transform(
      entity.pessoa?.nome,
      entity.pessoa?.nomeFantasia,
      entity.pessoa?.codigoIntegracaoErp
    );
    this.messageService
      .confirm(
        `Deseja realmente cancelar a conclusão da análise financeira do cliente ${nomeCliente}?`
      )
      .then((result) => {
        if (result) {
          this.clienteAnaliseFinanceiraConcluirHttpService
            .cancelarConcluir(entity.id)
            .pipe(take(1))
            .subscribe(
              () => {
                this.messageService.success(
                  '',
                  `Conclusão da Análise Financeira do cliente ${nomeCliente} cancelada com sucesso`
                );
                this.refresh();
              },
              (error: ApiError) =>
                this.messageService.error(error.message, error.cause, error)
            );
        }
      });
  }

  revisarAnaliseFinanceira(entity: Cliente) {
    this.analiseFinanceiraRevisarModal?.open(entity?.id);
  }

  onAfterRevisarAnaliseFinanceira() {
    this.refresh();
  }

  congelar(entity: Cliente) {
    this.clienteCarteiraCongelarModal?.insert(entity?.id);
  }

  descongelar(entity: Cliente) {
    if (!entity) {
      return;
    }
    const nomeCliente = this.clienteNomePipe.transform(
      entity.pessoa?.nome,
      entity.pessoa?.nomeFantasia,
      entity.pessoa?.codigoIntegracaoErp
    );
    this.messageService
      .confirm(
        `Deseja realmente descongelar o cliente ${nomeCliente}?`,
        'Confirme'
      )
      .then((result) => {
        if (result) {
          this.clienteCarteiraCongelarHttpService
            .descongelar(entity.id)
            .pipe(take(1))
            .subscribe(
              () => {
                this.messageService.success(
                  '',
                  `Cliente ${nomeCliente} descongelado com sucesso`
                );
                this.refresh();
              },
              (error: ApiError) =>
                this.messageService.error(error.message, error.cause, error)
            );
        }
      });
  }

  preBaixar(entity: Cliente) {
    this.clienteCarteiraPreBaixarModal?.insert(entity?.id);
  }

  editPreBaixa(entity: Cliente) {
    this.clienteCarteiraPreBaixarModal?.edit(entity.id);
  }

  cancelarPreBaixa(entity: Cliente) {
    if (!entity) {
      return;
    }
    const nomeCliente = this.clienteNomePipe.transform(
      entity.pessoa?.nome,
      entity.pessoa?.nomeFantasia,
      entity.pessoa?.codigoIntegracaoErp
    );
    this.messageService
      .confirm(
        `Deseja realmente cancelar a pré-baixa do cliente ${nomeCliente}?`,
        'Confirme'
      )
      .then((result) => {
        if (result) {
          this.clienteCarteiraPreBaixarHttpService
            .cancelarPreBaixa(entity.id)
            .pipe(take(1))
            .subscribe(
              () => {
                this.messageService.success(
                  '',
                  `Pré-baixa do cliente ${nomeCliente} cancelada com sucesso`
                );
                this.refresh();
              },
              (error: ApiError) =>
                this.messageService.error(
                  error.message,
                  `Erro ao cancelar a pré-baixa do cliente ${nomeCliente}`
                )
            );
        }
      });
  }

  baixar(entity: Cliente) {
    this.clienteCarteiraBaixarModal?.insert(entity?.id, entity?.motivoBaixa);
  }

  editBaixa(entity: Cliente) {
    this.clienteCarteiraBaixarModal?.edit(entity.id);
  }

  cancelarBaixa(entity: Cliente) {
    if (!entity) {
      return;
    }
    const nomeCliente = this.clienteNomePipe.transform(
      entity.pessoa?.nome,
      entity.pessoa?.nomeFantasia,
      entity.pessoa?.codigoIntegracaoErp
    );
    this.messageService
      .confirm(
        `Deseja realmente cancelar a baixa do cliente ${nomeCliente}?`,
        'Confirme'
      )
      .then((result) => {
        if (result) {
          this.clienteCarteiraBaixarHttpService
            .cancelarBaixa(entity.id)
            .pipe(take(1))
            .subscribe(
              () => {
                this.messageService.success(
                  '',
                  `Baixa do cliente ${nomeCliente} cancelada com sucesso`
                );
                this.refresh();
              },
              (error: ApiError) =>
                this.messageService.error(
                  error.message,
                  `Erro ao cancelar a baixa do cliente ${nomeCliente}`
                )
            );
        }
      });
  }

  ngOnDestroy(): void {
    this.sub$?.unsubscribe();
  }
}
